/**
 * PD-16691 — a signature question's answer: the drawn signature as a PNG, sent and stored
 * as a `data:image/png;base64,…` string in the submission's answers. Pure, so the page and
 * the server refuse exactly the same values.
 *
 * Only that one prefix is accepted: an answer is later drawn as an image in the CRM, and a
 * data URL of any other type (SVG can carry script) must never reach an `<img>` there.
 */

export const FORM_SIGNATURE_DATA_URL_PREFIX = 'data:image/png;base64,';

/**
 * The longest signature accepted, as characters of the whole data URL (about 150 KB). A
 * signature drawn on the pad is a few kilobytes to a few tens of kilobytes; the cap only
 * stops a page from sending a photograph.
 */
export const FORM_SIGNATURE_MAX_LENGTH = 150 * 1024;

/**
 * The largest JSON body the public form endpoints accept (crm-api main.ts), in bytes.
 * Express's default is 100 KB, less than one signature at its cap; 1 MB holds several
 * signatures plus every other answer at its own ceiling.
 */
export const FORM_SUBMIT_BODY_MAX_BYTES = 1024 * 1024;

/** The first eight bytes of every PNG file, as base64 writes them. */
const PNG_SIGNATURE_BASE64 = 'iVBORw0KGgo';

const BASE64_PATTERN = /^[A-Za-z0-9+/]+={0,2}$/;

export type FormSignatureCheckModel = 'ok' | 'too_long' | 'invalid_format';

/**
 * Whether a value is a signature the forms accept: at most FORM_SIGNATURE_MAX_LENGTH
 * characters, the PNG data URL prefix, then well-formed base64 of a PNG file. The length
 * is checked first, so an oversized value is never scanned.
 */
export function checkFormSignature(value: string): FormSignatureCheckModel {
	if (value.length > FORM_SIGNATURE_MAX_LENGTH) {
		return 'too_long';
	}
	if (!value.startsWith(FORM_SIGNATURE_DATA_URL_PREFIX)) {
		return 'invalid_format';
	}
	const payload = value.slice(FORM_SIGNATURE_DATA_URL_PREFIX.length);
	if (
		payload.length === 0 ||
		payload.length % 4 !== 0 ||
		!BASE64_PATTERN.test(payload) ||
		!payload.startsWith(PNG_SIGNATURE_BASE64)
	) {
		return 'invalid_format';
	}
	return 'ok';
}

/** A stored answer that can be drawn as the signature image. */
export function isFormSignatureDataUrl(value: unknown): value is string {
	return typeof value === 'string' && checkFormSignature(value) === 'ok';
}

/**
 * How a signature reads wherever the image itself does not belong (the notification
 * email, exports, notes, merge tags): "Signed", or nothing when it was not signed. The
 * data URL is never written out as text: mail clients block it and it bloats the message.
 */
export const FORM_SIGNATURE_SIGNED_TEXT = 'Signed';
